import React, { useMemo, useState } from 'react';
import { Check, ChevronDown, Plus, X } from 'lucide-react';
import {
    Command,
    CommandEmpty,
    CommandGroup,
    CommandInput,
    CommandItem,
    CommandList,
} from '../../../components/ui/command';
import { Popover, PopoverContent, PopoverTrigger } from '../../../components/ui/popover';
import { cn } from '../../../components/ui/utils';
import { ListTag, TagInput } from '../../../types/lists.types';
import { TAG_PILL_CLASS } from '../utils/lists-utils';

interface TagComboboxProps {
    /** Tags that already exist, offered as suggestions. */
    options: ListTag[];
    /** Current selection — existing tag ids and/or new tag names. */
    value: TagInput;
    onChange: (value: TagInput) => void;
    placeholder?: string;
    disabled?: boolean;
}

/**
 * Combobox that lets the user pick an existing tag or type a new one and create
 * it inline. New tags are carried as plain strings; the API resolves them to rows
 * on save, so nothing is written until the parent form is submitted.
 */
export const TagCombobox: React.FC<TagComboboxProps> = ({
    options,
    value,
    onChange,
    placeholder = 'Select or create tags',
    disabled = false,
}) => {
    const [open, setOpen] = useState(false);
    const [search, setSearch] = useState('');

    const selectedLabels = useMemo(
        () =>
            value.map((entry) => ({
                entry,
                label:
                    typeof entry === 'number'
                        ? options.find((o) => o.id === entry)?.name ?? `#${entry}`
                        : entry,
            })),
        [value, options]
    );

    const isSelected = (tag: ListTag) =>
        value.some((v) => v === tag.id || (typeof v === 'string' && v.toLowerCase() === tag.name.toLowerCase()));

    const toggle = (tag: ListTag) => {
        if (isSelected(tag)) {
            onChange(value.filter((v) => !(v === tag.id || (typeof v === 'string' && v.toLowerCase() === tag.name.toLowerCase()))));
        } else {
            onChange([...value, tag.id]);
        }
    };

    const remove = (entry: number | string) => onChange(value.filter((v) => v !== entry));

    const trimmed = search.trim();
    const matchesExisting = options.some((o) => o.name.toLowerCase() === trimmed.toLowerCase());
    const alreadyChosen = value.some(
        (v) => typeof v === 'string' && v.toLowerCase() === trimmed.toLowerCase()
    );
    const canCreate = trimmed.length > 0 && !matchesExisting && !alreadyChosen;

    const create = () => {
        onChange([...value, trimmed]);
        setSearch('');
    };

    return (
        <div className="space-y-2">
            <Popover open={open} onOpenChange={setOpen}>
                <PopoverTrigger asChild disabled={disabled}>
                    <button
                        type="button"
                        className={cn(
                            'flex h-9 w-full items-center justify-between rounded-md border border-gray-200 bg-white px-3 py-1 text-sm',
                            'hover:border-gray-300 focus:border-primary/20 focus:ring-1 focus:ring-primary/20 outline-none',
                            disabled && 'cursor-not-allowed opacity-50'
                        )}
                    >
                        <span className={cn('truncate', value.length === 0 && 'text-muted-foreground')}>
                            {value.length === 0 ? placeholder : `${value.length} selected`}
                        </span>
                        <ChevronDown size={16} className="shrink-0 opacity-50" />
                    </button>
                </PopoverTrigger>

                <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start">
                    <Command shouldFilter>
                        <CommandInput
                            placeholder="Search or type to create..."
                            value={search}
                            onValueChange={setSearch}
                        />
                        <CommandList>
                            {!canCreate && <CommandEmpty>No tags found.</CommandEmpty>}

                            {canCreate && (
                                <CommandGroup>
                                    <CommandItem value={`__create__${trimmed}`} onSelect={create}>
                                        <Plus size={16} className="mr-2" />
                                        Create "{trimmed}"
                                    </CommandItem>
                                </CommandGroup>
                            )}

                            {options.length > 0 && (
                                <CommandGroup>
                                    {options.map((tag) => (
                                        <CommandItem key={tag.id} value={tag.name} onSelect={() => toggle(tag)}>
                                            <Check
                                                size={16}
                                                className={cn('mr-2', isSelected(tag) ? 'opacity-100' : 'opacity-0')}
                                            />
                                            {tag.name}
                                        </CommandItem>
                                    ))}
                                </CommandGroup>
                            )}
                        </CommandList>
                    </Command>
                </PopoverContent>
            </Popover>

            {selectedLabels.length > 0 && (
                <div className="flex flex-wrap gap-1.5">
                    {selectedLabels.map(({ entry, label }) => (
                        <span key={String(entry)} className={cn(TAG_PILL_CLASS, 'inline-flex items-center gap-1')}>
                            {label}
                            {typeof entry === 'string' && (
                                <span className="text-[10px] uppercase tracking-wide opacity-60">new</span>
                            )}
                            {!disabled && (
                                <button
                                    type="button"
                                    onClick={() => remove(entry)}
                                    className="ml-0.5 opacity-60 hover:opacity-100"
                                    aria-label={`Remove ${label}`}
                                >
                                    <X size={12} />
                                </button>
                            )}
                        </span>
                    ))}
                </div>
            )}
        </div>
    );
};

export default TagCombobox;
